import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, switchMap } from 'rxjs';
import * as Selectors from '../selectors';
import {
    MarginCalculatorApiActions,
    MarginCalculatorComputationActions,
    MarginCalculatorPageActions,
    PayBillingActions
} from '../actions';
import { MarginCalculatorFormulaService } from '../../services/margin-calculator-formula.service';
import { MarginCalculatorBillingService } from '../../services/margin-calculator-pay-billing.service';
import { FormulaHelperService } from '../../services/formula-helper.service';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';

@Injectable()
export class BillingRateEffects {
    originalBillingRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorApiActions.billingRatesLoadedSuccess,
                MarginCalculatorPageActions.setMargin,
                MarginCalculatorPageActions.setShiftType
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectStringShiftTime),
                this.store.select(Selectors.selectShiftHours),
                this.store.select(Selectors.selectBillingRates),
                this.store.select(Selectors.selectFacility)
            ]),
            switchMap(([, shiftTime, shiftHours, billingRates, facility]) => {
                let originalBillRate = MarginCalculatorBillingService.findBaseBillRate(
                    shiftTime,
                    shiftHours,
                    billingRates
                );
                if (billingRates && facility && billingRates?.length === 1 && billingRates[0]?.isRangeBilling) {
                    originalBillRate = billingRates[0].rangeBillingMin;
                }
                return of(MarginCalculatorPageActions.setOriginalBillingRate({ billingRate: originalBillRate }));
            })
        );
    });

    billingRatesToaster$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(MarginCalculatorApiActions.billingRatesLoadedSuccess),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectBillingRates),
                this.store.select(Selectors.selectFacility)
            ]),
            switchMap(([, billingRates, facility]) => {
                if (billingRates && facility) {
                    if (billingRates.length === 1) {
                        if (billingRates[0].isRangeBilling) {
                            const originalBillRate = billingRates[0].rangeBillingMin;
                            return of(
                                showToasterMessage({
                                    message: `Facility has a range of rates ($${originalBillRate} - $${billingRates[0].rangeBillingMax}). \r\n Please verify rate with ${facility?.accountManagerName}`,
                                    duration: 5000
                                })
                            );
                        }
                    } else if (billingRates.length > 1) {
                        return of(
                            showToasterMessage({
                                message:
                                    'There is more than one bill rate for this facility. Please contact System Administrator.'
                            })
                        );
                    }
                }
                return of(
                    MarginCalculatorPageActions.noOp({
                        debug: 'billingRatesToaster$ effect: no toaster messages displayed'
                    })
                );
            })
        );
    });

    setBaseBillingRate$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorPageActions.setMargin,
                MarginCalculatorComputationActions.baseBillingRateComputedSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectBaseBillingRateValue),
                this.store.select(Selectors.selectBaseBillingRate),
                this.store.select(Selectors.selectOriginalBillingRate)
            ]),
            switchMap(([, billRate, baseBillingRate, originalBillingRate]) => {
                return of(
                    MarginCalculatorPageActions.setBaseBillingRate({
                        baseBillingRate: {
                            value: billRate,
                            originalBillingRate,
                            jobBillingRate: baseBillingRate?.jobBillingRate,
                            resetValidation: false,
                            resetChargebumpOncallBillRate: true
                        }
                    })
                );
            })
        );
    });

    setBaseBillingRateAlt$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorPageActions.setMargin,
                MarginCalculatorComputationActions.baseBillingRateAltComputedSuccess
            ),
            concatLatestFrom(() => [
                this.store.select(Selectors.selectBaseBillingRateAltValue),
                this.store.select(Selectors.selectBaseBillingRate),
                this.store.select(Selectors.selectBaseBillingRateAlt)
            ]),
            switchMap(([, billRateAlt, baseBillingRate, baseBillingRateAlt]) => {
                return of(
                    MarginCalculatorPageActions.setBaseBillingRateAlt({
                        baseBillingRateAlt: {
                            value: billRateAlt,
                            originalBillingRate: baseBillingRateAlt?.originalBillingRate,
                            jobBillingRate: baseBillingRate?.jobBillingRate,
                            resetValidation: false
                        }
                    })
                );
            })
        );
    });

    setShiftBonus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                MarginCalculatorApiActions.facilityLoadedSuccess,
                MarginCalculatorPageActions.setFacility,
                MarginCalculatorPageActions.setMargin,
                MarginCalculatorApiActions.marginLoadedSuccess,
                MarginCalculatorPageActions.setShiftHours,
                PayBillingActions.setPremiumRate,
                PayBillingActions.setMarginPayRate,
                MarginCalculatorPageActions.onMarginPayRateChanged,
                PayBillingActions.setBlendedPayRate
            ),
            concatLatestFrom(() => [this.store.select(Selectors.selectShiftBonusParams)]),
            switchMap(([, shiftBonusParams]) => {
                if (
                    shiftBonusParams.showShift &&
                    shiftBonusParams.otPayRate >
                        this._marginFormulaService.calculateMinPremiumPayRate(shiftBonusParams.basePayRate)
                ) {
                    return of(
                        PayBillingActions.setShiftBonus({
                            shiftBonus: FormulaHelperService.calculateShift(
                                shiftBonusParams.otPayRate,
                                shiftBonusParams.blendedPayRate,
                                shiftBonusParams.shiftHours
                            )
                        })
                    );
                }

                return of(PayBillingActions.setShiftBonus({ shiftBonus: 0 }));
            })
        );
    });

    constructor(
        private readonly store: Store,
        private readonly actions$: Actions,
        private readonly _marginFormulaService: MarginCalculatorFormulaService
    ) {}
}
